import React, { useState, useEffect } from 'react';
import { extractConfig, validateConfig, applyConfig } from '../utils/configExport';
import { 
  saveConfigToDatabase, 
  loadConfigFromDatabase, 
  deleteConfigFromDatabase, 
  getAllConfigurations 
} from '../utils/configStorage';
import useLocalization from '../hooks/useLocalization';

/**
 * Component for saving and loading calculator configurations from the database
 */
const DatabaseConfigIO = ({ state, onConfigApply, password }) => {
  const translations = useLocalization();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newConfigName, setNewConfigName] = useState('');
  const [newConfigDescription, setNewConfigDescription] = useState('');
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  
  // Text strings with defaults in case localization is not set up
  const {
    databaseConfigIO = {
      title: "Database Configurations",
      saveButton: "Save to Database",
      loadButton: "Load",
      deleteButton: "Delete",
      saveFormTitle: "Save Configuration",
      nameLabel: "Configuration Name",
      descriptionLabel: "Description (optional)",
      cancelButton: "Cancel",
      saveLabel: "Save",
      noConfigsMessage: "No saved configurations found",
      loadSuccess: "Configuration loaded successfully",
      saveSuccess: "Configuration saved successfully",
      deleteSuccess: "Configuration deleted successfully",
      error: "Error",
      loadingMessage: "Loading...",
      lastUpdated: "Last updated",
      confirmDelete: "Are you sure you want to delete this configuration?"
    }
  } = translations;

  // Load all configurations when component mounts
  useEffect(() => {
    loadConfigurations();
  }, []);

  /**
   * Load all configurations from the database
   */
  const loadConfigurations = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const configs = await getAllConfigurations();
      setConfigs(configs);
    } catch (err) {
      console.error('Error loading configurations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle saving a configuration to the database
   */
  const handleSave = async () => {
    if (!newConfigName.trim()) {
      setError('Configuration name is required');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await saveConfigToDatabase(state, newConfigName, newConfigDescription);
      
      // Clear form
      setNewConfigName('');
      setNewConfigDescription('');
      setShowSaveForm(false);
      
      // Reload configurations
      await loadConfigurations();
      
      alert(databaseConfigIO.saveSuccess);
    } catch (err) {
      console.error('Save error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle loading a configuration from the database
   */
  const handleLoad = async (configName) => {
    setLoading(true);
    setError(null);
    
    try {
      const config = await loadConfigFromDatabase(configName);
      
      // Apply the configuration
      const stateUpdates = applyConfig(config);
      onConfigApply(stateUpdates);
      
      alert(databaseConfigIO.loadSuccess);
    } catch (err) {
      console.error('Load error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle deleting a configuration from the database
   */
  const handleDelete = async (configName) => {
    if (!window.confirm(databaseConfigIO.confirmDelete)) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await deleteConfigFromDatabase(configName);
      
      // Reload configurations
      await loadConfigurations();
      
      alert(databaseConfigIO.deleteSuccess);
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Format date string for display
   */
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (e) {
      return dateString;
    }
  };

  /**
   * Render save configuration form
   */
  const renderSaveForm = () => {
    return (
      <div className="mt-4 bg-gray-50 p-4 rounded-md border border-gray-200">
        <h3 className="text-lg font-medium mb-3">{databaseConfigIO.saveFormTitle}</h3>
        
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {databaseConfigIO.nameLabel}
          </label>
          <input
            type="text"
            value={newConfigName}
            onChange={(e) => setNewConfigName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder="My Configuration"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {databaseConfigIO.descriptionLabel}
          </label>
          <textarea
            value={newConfigDescription}
            onChange={(e) => setNewConfigDescription(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            rows="2"
            placeholder="Description of this configuration"
          />
        </div>
        
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setShowSaveForm(false)}
            className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {databaseConfigIO.cancelButton}
          </button>
          <button
            onClick={handleSave}
            disabled={loading || !newConfigName.trim()}
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {loading ? databaseConfigIO.loadingMessage : databaseConfigIO.saveLabel}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 mb-6">
      <h2 className="text-xl font-semibold mb-4">{databaseConfigIO.title}</h2>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          <strong className="font-bold">{databaseConfigIO.error}:</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      )}
      
      <button
        onClick={() => setShowSaveForm(true)}
        disabled={loading}
        className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md mb-4"
      >
        {databaseConfigIO.saveButton}
      </button>
      
      {showSaveForm && renderSaveForm()}
      
      <div className="mt-4">
        {loading && !showSaveForm ? (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-indigo-500 border-t-transparent"></div>
            <p className="mt-2 text-gray-600">{databaseConfigIO.loadingMessage}</p>
          </div>
        ) : configs.length === 0 ? (
          <p className="text-gray-500 text-center py-4">{databaseConfigIO.noConfigsMessage}</p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {configs.map((config) => (
              <li key={config.name} className="py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{config.name}</h3>
                    {config.description && (
                      <p className="text-sm text-gray-500">{config.description}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {databaseConfigIO.lastUpdated}: {formatDate(config.updated_at)}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleLoad(config.name)}
                      disabled={loading}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {databaseConfigIO.loadButton}
                    </button>
                    <button
                      onClick={() => handleDelete(config.name)}
                      disabled={loading}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                    >
                      {databaseConfigIO.deleteButton}
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default DatabaseConfigIO;